import React, { useState } from 'react';
import { Button, ButtonGroup, Spinner } from 'react-bootstrap';
import { FaExternalLinkAlt, FaRedo, FaUndo } from 'react-icons/fa';
import { useRegistrationViewUrlQuery } from '../../queries/registrationReview.query';

const BROWSER_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const PREVIEW_HEIGHT = '65vh';

type Props = {
  fileId: string;
  fileName: string;
  contentType: string;
  /** Only the slide on screen (and its neighbours) should fetch a signed URL. */
  active: boolean;
};

function RegistrationPreview({ fileId, fileName, contentType, active }: Props) {
  const [rotation, setRotation] = useState(0);
  const {
    data: url,
    isLoading,
    isError
  } = useRegistrationViewUrlQuery(active ? fileId : undefined);

  const type = (contentType || '').toLowerCase();
  const isImage = BROWSER_IMAGE_TYPES.includes(type);
  const isPdf = type === 'application/pdf';

  if (!active) {
    return <div style={{ height: PREVIEW_HEIGHT }} />;
  }

  if (isLoading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: PREVIEW_HEIGHT }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (isError || !url) {
    return (
      <div
        className="d-flex align-items-center justify-content-center text-danger"
        style={{ height: PREVIEW_HEIGHT }}>
        Could not load this registration file.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="small text-muted text-truncate" title={fileName}>
          {fileName}
        </span>
        <div className="d-flex gap-2">
          {isImage && (
            <ButtonGroup size="sm">
              <Button
                variant="outline-secondary"
                title="Rotate left"
                onClick={() => setRotation((r) => (r + 270) % 360)}>
                <FaUndo />
              </Button>
              <Button
                variant="outline-secondary"
                title="Rotate right"
                onClick={() => setRotation((r) => (r + 90) % 360)}>
                <FaRedo />
              </Button>
            </ButtonGroup>
          )}
          <Button
            size="sm"
            variant="outline-secondary"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open full size in a new tab">
            <FaExternalLinkAlt />
          </Button>
        </div>
      </div>

      <div
        className="d-flex align-items-center justify-content-center bg-light border rounded overflow-hidden"
        style={{ height: PREVIEW_HEIGHT }}>
        {isImage && (
          <img
            src={url}
            alt={`Registration ${fileName}`}
            style={{
              // Sideways, the image's width becomes its on-screen height.
              maxWidth: rotation % 180 === 0 ? '100%' : PREVIEW_HEIGHT,
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.2s'
            }}
          />
        )}
        {isPdf && (
          <iframe
            src={url}
            title={`Registration ${fileName}`}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        )}
        {!isImage && !isPdf && (
          <div className="text-center text-muted p-4">
            <p className="mb-2">
              This file type ({contentType}) can&apos;t be previewed in the browser.
            </p>
            <Button variant="primary" href={url} target="_blank" rel="noopener noreferrer">
              Open file
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RegistrationPreview;
